/**
 * Disclosure Tracking Service
 *
 * Handles queries for detailed disclosure tracking data:
 * - Disclosure events per loan
 * - Per-recipient signature status
 * - Documents list
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  DisclosureEventWithRecipients,
  DisclosureEvent,
  DisclosureRecipient,
  DisclosureDocument,
  DisclosureHistory
} from '@/types/disclosure-tracking';

const log = createComponentLogger('DisclosureTrackingService', 'disclosure-tracking');

/**
 * Transform raw recipient row to UI format
 */
function transformRecipient(row: DisclosureEventWithRecipients['recipients'][0]): DisclosureRecipient {
  return {
    id: row.id,
    name: row.recipient_name,
    email: row.recipient_email,
    type: row.recipient_type,
    role: row.role_description,
    status: row.status,
    signedDate: row.signed_date,
    viewedDate: row.viewed_date
  };
}

/**
 * Transform raw event row to UI format
 */
function transformEvent(row: DisclosureEventWithRecipients): DisclosureEvent {
  const documents: DisclosureDocument[] = (row.documents || []).map(doc => ({
    formName: doc.formName || '',
    formType: doc.formType || '',
    signatureType: doc.signatureType || ''
  }));

  const recipients = (row.recipients || []).map(transformRecipient);

  return {
    id: row.id,
    logId: row.encompass_log_id,
    type: row.disclosure_type,
    formType: row.form_type,
    disclosedDate: row.disclosed_date,
    disclosedBy: row.disclosed_by,
    disclosedMethod: row.disclosed_method,
    status: row.status,
    hasItop: row.has_itop,
    itopDate: row.itop_date,
    changedCircumstance: row.changed_circumstance,
    changeReason: row.change_reason,
    documents,
    recipients,
    // New fields for Encompass parity
    leSent: row.le_sent ?? false,
    cdSent: row.cd_sent ?? false,
    safeHarborSent: row.safe_harbor_sent ?? false,
    providerListSent: row.provider_list_sent ?? false,
    brokerDisclosed: row.broker_disclosed ?? false,
    documentCount: row.document_count ?? (row.documents?.length || 0),
    borrowerPair: row.borrower_pair ?? null,
    // Read canonical column when present; fall back to legacy mirrors so rows
    // written before the new column was added still resolve correctly during
    // the transition window.
    includedInTimeline:
      row.included_in_timeline ??
      !(row.exclude_from_timeline || row.exclude_from_cd_timeline)
  };
}

class DisclosureTrackingService {
  /**
   * Get disclosure history for a specific loan
   */
  async getDisclosureHistory(encompassLoanId: string): Promise<DisclosureHistory | null> {
    try {
      const startTime = Date.now();

      const { data, error } = await supabase
        .from('disclosure_events')
        .select(`
          *,
          recipients:disclosure_recipients(*)
        `)
        .eq('encompass_loan_id', encompassLoanId)
        .eq('status', 'active')
        .order('disclosed_date', { ascending: true });

      if (error) {
        log.error('Failed to fetch disclosure history', { encompassLoanId }, error);
        return null;
      }

      if (!data || data.length === 0) {
        return {
          loanId: encompassLoanId,
          events: [],
          totalEvents: 0,
          signedCount: 0,
          pendingCount: 0
        };
      }

      const events = (data as DisclosureEventWithRecipients[]).map(transformEvent);

      // Count signature stats across all recipients
      let signedCount = 0;
      let pendingCount = 0;

      for (const event of events) {
        for (const recipient of event.recipients) {
          if (recipient.status === 'signed') {
            signedCount++;
          } else if (recipient.status === 'pending' || recipient.status === 'viewed') {
            pendingCount++;
          }
        }
      }

      log.info('Fetched disclosure history', {
        encompassLoanId,
        eventCount: events.length,
        duration: Date.now() - startTime
      });

      return {
        loanId: encompassLoanId,
        events,
        totalEvents: events.length,
        signedCount,
        pendingCount
      };
    } catch (error) {
      log.error('Error in getDisclosureHistory', { encompassLoanId }, error as Error);
      return null;
    }
  }

  /**
   * Get a single disclosure event by ID
   */
  async getDisclosureEvent(eventId: string): Promise<DisclosureEvent | null> {
    try {
      const { data, error } = await supabase
        .from('disclosure_events')
        .select(`
          *,
          recipients:disclosure_recipients(*)
        `)
        .eq('id', eventId)
        .single();

      if (error) {
        log.error('Failed to fetch disclosure event', { eventId }, error);
        return null;
      }

      return transformEvent(data as DisclosureEventWithRecipients);
    } catch (error) {
      log.error('Error in getDisclosureEvent', { eventId }, error as Error);
      return null;
    }
  }

  /**
   * Check if a loan has any disclosure events
   */
  async hasDisclosures(encompassLoanId: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('disclosure_events')
        .select('*', { count: 'exact', head: true })
        .eq('encompass_loan_id', encompassLoanId)
        .eq('status', 'active');

      if (error) {
        log.error('Failed to check disclosures', { encompassLoanId }, error);
        return false;
      }

      return (count || 0) > 0;
    } catch (error) {
      log.error('Error in hasDisclosures', { encompassLoanId }, error as Error);
      return false;
    }
  }

  /**
   * Get disclosure stats for a loan (for summary display)
   */
  async getDisclosureStats(encompassLoanId: string): Promise<{
    leCount: number;
    cdCount: number;
    totalRecipients: number;
    signedRecipients: number;
  }> {
    try {
      const history = await this.getDisclosureHistory(encompassLoanId);

      if (!history) {
        return { leCount: 0, cdCount: 0, totalRecipients: 0, signedRecipients: 0 };
      }

      let leCount = 0;
      let cdCount = 0;
      let totalRecipients = 0;
      let signedRecipients = 0;

      for (const event of history.events) {
        if (event.formType === 'LE') {
leCount++;
} else if (event.formType === 'CD') {
cdCount++;
}

        for (const recipient of event.recipients) {
          totalRecipients++;
          if (recipient.status === 'signed') {
signedRecipients++;
}
        }
      }

      return { leCount, cdCount, totalRecipients, signedRecipients };
    } catch (error) {
      log.error('Error in getDisclosureStats', { encompassLoanId }, error as Error);
      return { leCount: 0, cdCount: 0, totalRecipients: 0, signedRecipients: 0 };
    }
  }
}

export const disclosureTrackingService = new DisclosureTrackingService();
